import { nowImmediate } from '../lib/dateTime';
import { getRulesStore } from '../storage/stores';
import { RoutineRule, ROUTINE_RULE_TYPE } from '../types';

export const RoutineRuleStateType = {
  NO_ROUTINE_ACTIVE: 'noRoutineActive',
  NO_ROUTINE_ACTIVE_BUT_EXPECTED_SOON: 'noRoutineActiveButExpectedSoon',
  ROUTINE_ACTIVE: 'routineActive',
} as const;

export type RoutineRuleState =
  | { readonly state: typeof RoutineRuleStateType.NO_ROUTINE_ACTIVE }
  | {
      readonly state: typeof RoutineRuleStateType.NO_ROUTINE_ACTIVE_BUT_EXPECTED_SOON;
      readonly startsInSeconds: number;
    }
  | {
      readonly state: typeof RoutineRuleStateType.ROUTINE_ACTIVE;
      readonly routine: RoutineRule;
      readonly activeUntilInSeconds: number;
    };

export const RoutineContentFilteringDecisionType = {
  ALLOW: 'allow',
  BLOCK: 'block',
} as const;

export type RoutineContentFilteringDecision =
  | { readonly type: typeof RoutineContentFilteringDecisionType.ALLOW }
  | {
      readonly type: typeof RoutineContentFilteringDecisionType.BLOCK;
      readonly categoriesMatch: readonly number[];
      readonly activeUntilInSeconds: number;
    };

export type RoutineRuleCheckerDeps = {
  getRulesStore: typeof getRulesStore;
  nowImmediate: () => number;
};

export type RoutineRuleChecker = {
  checkScreenFreeTime: () => RoutineRuleState;
  checkContentFiltering: (
    categories: readonly number[]
  ) => RoutineContentFilteringDecision | undefined;
  countsTowardsDailyTimeQuota: () => boolean;
};

const noRoutineActive = (): RoutineRuleState => ({
  state: RoutineRuleStateType.NO_ROUTINE_ACTIVE,
});

const noRoutineActiveButExpectedSoon = (
  startsInSeconds: number
): RoutineRuleState => ({
  state: RoutineRuleStateType.NO_ROUTINE_ACTIVE_BUT_EXPECTED_SOON,
  startsInSeconds,
});

const routineActive = (
  routine: RoutineRule,
  activeUntilInSeconds: number
): RoutineRuleState => ({
  state: RoutineRuleStateType.ROUTINE_ACTIVE,
  routine,
  activeUntilInSeconds,
});

export const parseRoutineActiveUntilInSeconds = (
  activeUntil: string | undefined
): number => {
  if (!activeUntil) {
    return 0;
  }

  const trimmed = activeUntil.trim();
  if (trimmed !== '' && /^\d+$/.test(trimmed)) {
    const value = Math.trunc(Number(trimmed));
    return Math.max(value, 0);
  }

  const ms = Date.parse(activeUntil);
  if (Number.isNaN(ms)) {
    return 0;
  }

  const value = Math.floor(ms / 1000);
  return Math.max(value, 0);
};

export const isRoutineActiveAt = (
  routine: RoutineRule,
  nowMs: number
): boolean => {
  const activeUntilInSeconds = parseRoutineActiveUntilInSeconds(
    routine.activeUntil
  );
  return activeUntilInSeconds > Math.floor(nowMs / 1000);
};

export const findActiveRoutineRule = (
  routines: readonly RoutineRule[] | undefined,
  nowMs: number
): RoutineRule | undefined =>
  routines?.find(routine => isRoutineActiveAt(routine, nowMs));

/**
 * The stored next start can be days away; only expose the pre-start state
 * inside the notification window so regular verdict checks stay unchanged.
 */
const LOOK_AHEAD_SECONDS = 15 * 60;

export const createRoutineRuleChecker = (
  deps: RoutineRuleCheckerDeps
): RoutineRuleChecker => {
  const findActiveRoutine = (): RoutineRule | undefined =>
    findActiveRoutineRule(
      deps.getRulesStore().getRoutines(),
      deps.nowImmediate()
    );

  const getActiveRoutineByType = <T extends RoutineRule['type']>(
    type: T
  ): Extract<RoutineRule, { type: T }> | undefined => {
    const routine = findActiveRoutine();
    return routine?.type === type
      ? (routine as Extract<RoutineRule, { type: T }>)
      : undefined;
  };

  const checkScreenFreeTime = (): RoutineRuleState => {
    const routine = getActiveRoutineByType(ROUTINE_RULE_TYPE.SCREEN_FREE_TIME);
    if (routine) {
      return routineActive(
        routine,
        parseRoutineActiveUntilInSeconds(routine.activeUntil)
      );
    }

    const nextStartsAtInSeconds = deps
      .getRulesStore()
      .get()?.nextScreenFreeTimeRoutineStartsAtInSeconds;
    if (nextStartsAtInSeconds === undefined) {
      return noRoutineActive();
    }

    const nowSec = Math.floor(deps.nowImmediate() / 1000);
    const startsInSeconds = nextStartsAtInSeconds - nowSec;
    if (startsInSeconds <= 0 || startsInSeconds > LOOK_AHEAD_SECONDS) {
      return noRoutineActive();
    }

    return noRoutineActiveButExpectedSoon(startsInSeconds);
  };

  const checkContentFiltering = (
    categories: readonly number[]
  ): RoutineContentFilteringDecision | undefined => {
    const routine = getActiveRoutineByType(ROUTINE_RULE_TYPE.CONTENT_FILTERING);
    if (!routine) {
      return undefined;
    }

    const exceptions = routine.domainCategoriesExceptions;
    const hasExceptionCategory =
      exceptions.length > 0 &&
      categories.some(category => exceptions.includes(category));

    if (routine.defaultDomainCategoriesAction === 'BLOCK_ALL') {
      if (hasExceptionCategory) {
        return { type: RoutineContentFilteringDecisionType.ALLOW };
      }

      const categoriesMatch = categories.filter(
        category => !exceptions.includes(category)
      );
      return {
        type: RoutineContentFilteringDecisionType.BLOCK,
        categoriesMatch,
        activeUntilInSeconds: parseRoutineActiveUntilInSeconds(
          routine.activeUntil
        ),
      };
    }

    if (!hasExceptionCategory) {
      return { type: RoutineContentFilteringDecisionType.ALLOW };
    }

    const categoriesMatch = exceptions.filter(category =>
      categories.includes(category)
    );
    return {
      type: RoutineContentFilteringDecisionType.BLOCK,
      categoriesMatch,
      activeUntilInSeconds: parseRoutineActiveUntilInSeconds(
        routine.activeUntil
      ),
    };
  };

  const countsTowardsDailyTimeQuota = (): boolean =>
    findActiveRoutine()?.countsTowardsDailyTimeQuota ?? true;

  return {
    checkScreenFreeTime,
    checkContentFiltering,
    countsTowardsDailyTimeQuota,
  };
};

export const routineRuleChecker = createRoutineRuleChecker({
  getRulesStore,
  nowImmediate,
});
